/**
 * The only randomness in the game, and it runs before the first step() does.
 *
 * Architecture rule 2 (CLAUDE.md): step() is deterministic. A seeded stream is
 * what lets map generation be random without making the simulation random.
 */

/** A stream of numbers in [0, 1). Pure: same seed, same stream, always. */
export type Rng = () => number;

/**
 * mulberry32. 32-bit state, one multiply chain, period 2^32.
 *
 * Not Math.random(): it cannot be seeded, so a map could not be reproduced from
 * a save, a bug report could not carry its map, and a world could not regrow the
 * same way twice. Not crypto.getRandomValues(): same problem, plus it is a
 * browser API and src/core/ does not reach for those.
 *
 * The increment is mixed before it is used, so adjacent seeds produce
 * uncorrelated streams — which is what lets growth step n use `seed + n`.
 */
export function rng(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Uniform in [lo, hi). */
export const between = (r: Rng, lo: number, hi: number): number => lo + r() * (hi - lo);

/** Fisher-Yates on a copy. The input is never mutated. */
export function shuffled<T>(r: Rng, items: readonly T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}
